import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {tabType, getNavigationImageSrc} from '../../../utils/editorUtils';
import {useToggle} from '../../../utils/hooks';
import { Tooltip} from '@mui/material';
import {makeStyles} from '@mui/styles'
import {hasValue} from '../../../utils/utils';
import SMCIcon from '../../Simple/SMCIcon';
import SMCHighlighter from '../../Simple/SMCHighlighter';

function EditorSidebarFolder({
    first,
    last,
    onChoose,
    children,
    icon,
    title,
    defaultOpen,
    forceOpen,
    hasError,
    childrenWithError,
    highlightText,
    hide
}) {
    const classes = useStyles();
    const [folderOpen, toggleFolderOpen] = useToggle(hasValue(defaultOpen) ? defaultOpen : false);
    
    const isOpen = useMemo(() => folderOpen || forceOpen, [folderOpen, forceOpen]);

    let navigatorImgSrc;
    let tabTypeBase = isOpen ? tabType.OPEN_FOLDER : tabType.FOLDER;
    
    if (first) {
        navigatorImgSrc = getNavigationImageSrc(tabTypeBase.FIRST);
    } else if (last) {
        navigatorImgSrc = getNavigationImageSrc(tabTypeBase.LAST);
    } else {
        navigatorImgSrc = getNavigationImageSrc(tabTypeBase.MIDDLE);
    }

    const onClickText = () => {
        toggleFolderOpen();
        if (onChoose) {
            onChoose();
        }
    }
    const onClickNavButton = () => {
        toggleFolderOpen();
    }

    const childrenCount = React.Children.count(children);

    return (
        <div style={{ display: hide ? 'none' : 'block' }}>
            <div>
                <table cellSpacing="0" cellPadding="0" className={classes.folderRoot}>
                    <tbody>
                        <tr>
                            <td className={classes.imageCell} onClick={onClickNavButton}>
                                <img alt="" src={navigatorImgSrc} className={classes.navigationImage} />
                            </td>
                            <td className={classes.imageCell}>
                                <img alt="" src={icon} className={classes.displayIcon} />
                            </td>
                            <td onClick={onClickText} className={classes.defaultTab}>
                                <SMCHighlighter
                                    className={classes.defaultTabText}
                                    highlight={highlightText}
                                    text={title}
                                />
                                {
                                    hasError && (
                                        <div className={classes.tabErrorIconRoot}>
                                            <Tooltip
                                                placement="bottom-start"
                                                title={childrenWithError && (
                                                    <div style={{ fontSize: '12px' }}>
                                                        Error in pages:{childrenWithError.map(pageId => <React.Fragment key={pageId}><br /> - {pageId}</React.Fragment>)}
                                                    </div>
                                                )}
                                            >
                                                <div>
                                                    <SMCIcon icon="warning" />
                                                </div>
                                            </Tooltip>
                                        </div>
                                    )
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {
                isOpen &&
                React.Children.map(children, (child, index) => {
                    if (child) {
                        return React.cloneElement(child, {
                            key: index,
                            inFolder: true,
                            inLastFolder: last,
                            first: (index === 0),
                            last: (index === (childrenCount - 1)),
                        });
                    } else {
                        return child;
                    }
                })
            }
        </div>
    );
}

const useStyles = makeStyles({
    folderRoot: {
        padding: '14px 0 0 0',
        margin: '0'
    },
    imageCell: {
        width: '18px',
        height: '18px',
    },
    navigationImage: {
        padding: '0',
        margin: '0',
        verticalAlign: "middle"
    },
    displayIcon: {
        padding: '0',
        margin: '0',
        width: '16px',
        height: '16px'
    },
    warningIcon: {
        position: 'relative',
        height: '14px',
        width: '14px',
        right: '-9px',
        top: '-8px'
    },
    defaultTab: {
        userSelect: 'none',
        cursor: 'pointer',
        padding: '5px 5px 0px 5px',
        textAlign: 'start',
        display: 'inline-flex',
        margin: '0',
        textDecoration: 'none !important',
        minHeight: '23px',
        width: '160px',
        '&:hover': {
            backgroundColor: '#e0e4e8'
        }
    },
    defaultTabText: {
        fontFamily: 'Open Sans',
        fontSize: '10pt',
        width: '140px',
    },
    tabErrorIconRoot: {
        width: '20px'
    }
});


EditorSidebarFolder.propType = {
    first: PropTypes.bool,
    last: PropTypes.bool,
    onChoose: PropTypes.func,
    children: PropTypes.node.isRequired,
    icon: PropTypes.string,
    title: PropTypes.string.isRequired,
}

export default EditorSidebarFolder;